<script setup lang="ts">
import { onMounted, reactive, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useDebounceFn } from '@vueuse/core';
import { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useHotelStore } from '@/store/HotelStore';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Select from 'primevue/select';
import HotelDateTypes from './HotelDateTypes.vue';

interface Props {
  title: string;
  excludeRoomCategory: object;
  hotel: object;
  isEdit: boolean;
  destinations: [];
}
const props = defineProps<Props>();

const emits = defineEmits<{
  addNewCategory: [categoryInfo: any];
  removeCategory: [categoryInfo: any];
  addNewBlackoutDate: [categoryInfo: any];
  removeBlackoutDate: [hotelInfo: any];
  changeRoomCategory: [roomInfo: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const category = ref([]);
const currentLanguage = getCurrentLocale();
const hotelStore = useHotelStore();
const roomCategory = ref([]);
const isLoading = ref(false);

const minDateRangeValue = reactive(new Date(DateTime.utc().plus({ days: 60 })));
const maxDateRangeValue = reactive(new Date(DateTime.utc().plus({ days: 67 })));

const airportCode = computed(() => props.hotel?.destSVId);

let payload = reactive({
  branch: props.hotel?.destSVId?.code ?? 'VA',
  createQuote: 'N',
  dateDep: formatDate(minDateRangeValue, 'yyyyLLdd'),
  dateRet: formatDate(maxDateRangeValue, 'yyyyLLdd'),
  duration: 7,
  destDep: props.hotel?.destSVId?.airportCode,
  groupEvent: 'LE',
  header: 'FITGROUPVERIF-RQ',
  language: currentLanguage.toUpperCase(),
  nbAdult: 10,
  net: 'Y',
  productCode: props.hotel?.hCode,
  tourOp: 'VAC'
});

const addNewCategory = (type: string) => {
  emits('addNewCategory', type);
};

const removeCategory = (indexItem: any, type: string) => {
  emits('removeCategory', {
    indexItem: indexItem,
    type: type
  });
};

const addNewBlackoutDate = (indexItem: number, type: string) => {
  emits('addNewBlackoutDate', {
    indexItem: indexItem,
    type: type
  });
};

const removeBlackoutDate = (event: any, categoryIndex: number, type: string) => {
  emits('removeBlackoutDate', {
    dateIndex: event,
    categoryIndex: categoryIndex,
    type: type
  });
};

const landOnlyCall = async (payload: any) => {
  isLoading.value = true;
  return hotelStore
    .getHotelRoomCategory(payload)
    .then((response) => {
      roomCategory.value = response?.data?.error ? [] : response?.data;
      if (roomCategory.value.length === 0) {
        toast.add({ severity: 'error', summary: '', detail: t('notification.no_data_room_categories'), life: 5000 });
      }
      return roomCategory.value;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

const getListRoomCategory = useDebounceFn(async () => {
  if (props.hotel.destSVId?.airportCode !== undefined) {
    return landOnlyCall(payload);
  }
  if (!props.isEdit && props.hotel.destSVId) {
    const destinationSVId = props.destinations?.filter((dest) => dest?.code === props.hotel.destSVId);
    payload = {
      ...payload,
      branch: destinationSVId[0]?.code,
      destDep: destinationSVId[0]?.airportCode
    };
    return landOnlyCall(payload);
  }
}, 700);

const changeRoomCategory = (event: any, index: number) => {
  emits('changeRoomCategory', {
    room: event.value?.room,
    roomName: event.value?.roomDescription,
    index: index
  });
};

const handleFormatRoom = (list: any) => {
  const listRoom = list?.map((room: any) => {
    return roomCategory.value?.find((item: any) => {
      return item?.room === room?.category;
    });
  });
  return listRoom;
};

const filterOptions = (roomsSelected: any) => {
  if (roomCategory.value.length) {
    return roomCategory.value.filter((room: any) => !roomsSelected.some((selected: any) => selected?.room === room?.room));
  }
};

const clearRoomCategory = () => {
  roomCategory.value = [];
  category.value = [];
};

defineExpose({
  clearRoomCategory
});

watch(
  props.hotel,
  async (newVal) => {
    payload = {
      ...payload,
      branch: newVal?.destSVId?.code,
      destDep: newVal?.destSVId?.airportCode,
      productCode: newVal?.hCode
    };
    category.value = handleFormatRoom(props.excludeRoomCategory.categories);
  },
  { deep: true }
);

watch(
  airportCode,
  async (newVal) => {
    payload = {
      ...payload,
      branch: newVal?.code,
      destDep: newVal?.airportCode
    };
    await getListRoomCategory();
  },
  { deep: true }
);

onMounted(async () => {
  await getListRoomCategory();
  category.value = handleFormatRoom(props.excludeRoomCategory.categories);
});
</script>
<template>
  <div class="hotel-exclude-room">
    <div class="flex gap-2 mb-2">
      <div>{{ title }}</div>
      <div class="block text-primary cursor-pointer underline add-category" :class="roomCategory.length === 0 || category.length === roomCategory.length ? 'disabled' : ''" @click="addNewCategory('categories')">
        <i v-if="isLoading" class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
        <span>
          +
          {{ $t('common.add_category') }}
        </span>
      </div>
      <div class="block text-blue cursor-pointer underline ml-2" @click="addNewCategory('manualCategories')">+ {{ $t('management.hotel.add_manual_category') }}</div>
    </div>
    <div class="flex grid" v-for="(exclude, index) in excludeRoomCategory.categories" :key="index">
      <div class="col-4">
        <div class="w-full">
          <label for="select_room_category">{{ $t('management.hotel.select_room_category') }}</label>
          <Select v-model="category[index]" :options="filterOptions(category)" optionLabel="room" :disabled="roomCategory.length === 0" @change="changeRoomCategory($event, index)" :placeholder="$t('advance_quote.form.select_room')" showClear>
            <template #value="{ value, placeholder }">
              <div v-if="value" class="flex items-center">
                <div>{{ value.room }}</div>
              </div>
              <div v-else>{{ placeholder }}</div>
            </template>
            <template #option="slotProps">
              <div class="flex items-center">
                <div>{{ getTextVariant(currentLanguage, slotProps.option.roomDescription, slotProps.option.roomDescriptionFr) }} ({{ slotProps.option.room }})</div>
              </div>
            </template>
          </Select>
          <div class="action font-size-12">
            <div class="text-primary cursor-pointer underline add-black-out-date" :class="roomCategory.length === 0 ? 'disabled' : ''" @click="addNewBlackoutDate(index, 'categories')">+ {{ $t('management.hotel.add_blackout_date') }}</div>
            <div class="text-primary cursor-pointer underline remove-category" :class="roomCategory.length === 0 ? 'disabled' : ''" @click="removeCategory(index, 'categories')">- {{ $t('common.delete_category') }}</div>
          </div>
        </div>
      </div>
      <div class="col-8 flex flex-wrap">
        <HotelDateTypes :disable="roomCategory.length === 0" class="remove-blackout-date" :dateRanges="exclude.ranges" @removeDate="removeBlackoutDate($event, index, 'categories')" />
      </div>
    </div>

    <div class="flex grid" v-for="(exclude, index) in excludeRoomCategory.manualCategories" :key="'manual' + index">
      <div class="col-4">
        <div class="w-full mt-3">
          <label for="select_room_category">{{ $t('management.hotel.select_room_category') }} ({{ $t('management.hotel.manual') }})</label>
          <AutoComplete v-model="exclude.categories" inputId="inputChip" :selectionLimit="200" multiple fluid :typeahead="false" />
          <div class="action font-size-12">
            <div class="text-primary cursor-pointer underline add-black-out-date" @click="addNewBlackoutDate(index, 'manualCategories')">+ {{ $t('management.hotel.add_blackout_date') }}</div>
            <div class="text-primary cursor-pointer underline remove-category" @click="removeCategory(index, 'manualCategories')">- {{ $t('common.delete_category') }}</div>
          </div>
        </div>
      </div>
      <div class="col-8 flex flex-wrap mt-3">
        <HotelDateTypes :dateRanges="exclude.ranges" @removeDate="removeBlackoutDate($event, index, 'manualCategories')" />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.font-size-12 {
  font-size: 12px;
}
.action {
  display: flex;
  justify-content: space-between;
}
.text-blue {
  color: #0c08e9;
}
.disabled {
  pointer-events: none;
  opacity: 0.5;
  background-color: #f0f0f0;
}
</style>
